import { useState } from "react";
import { FaCheckCircle, FaMinus, FaPlus, FaShieldAlt, FaShoppingCart, FaTimes, FaTruck, FaUndo } from "react-icons/fa";
import { Product } from "../ShopFront";
import { StarRating } from "./StarRating";

export const QuickViewModal = ({ product, onClose, addToCart }: { product: Product | null; onClose: () => void; addToCart: () => void; }) => {
    const [quantity, setQuantity] = useState(1);

    if (!product) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="relative p-6">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        <FaTimes className="text-2xl" />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="relative">
                            <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-80 object-cover rounded-2xl"
                            />
                            {product.isSale && (
                                <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                    -{product.discount}%
                                </span>
                            )}
                        </div>
                        <div>
                            <h2 className="font-primary text-3xl font-bold text-gray-800 mb-2">{product.name}</h2>
                            <div className="flex items-center gap-3 mb-4">
                                <StarRating rating={product.rating} />
                                <span className="text-sm text-gray-500">{product.reviews} reviews</span>
                            </div>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="font-primary text-3xl font-bold text-blue-600">${product.price}</span>
                                {product.originalPrice && (
                                    <span className="text-lg text-gray-400 line-through">${product.originalPrice}</span>
                                )}
                            </div>
                            <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>
                            {product.features && (
                                <div className="mb-6">
                                    <h4 className="font-primary font-semibold text-gray-800 mb-2">Features</h4>
                                    <ul className="grid grid-cols-2 gap-2">
                                        {product.features.map((feature, index) => (
                                            <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                                <FaCheckCircle className="text-green-500" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="flex items-center gap-4 mb-6">
                                <div className="flex items-center gap-2 border border-gray-200 rounded-lg">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="px-3 py-2 hover:bg-gray-100 transition-colors duration-200"
                                    >
                                        <FaMinus className="text-sm" />
                                    </button>
                                    <span className="w-12 text-center font-primary font-semibold">{quantity}</span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="px-3 py-2 hover:bg-gray-100 transition-colors duration-200"
                                    >
                                        <FaPlus className="text-sm" />
                                    </button>
                                </div>
                                <button onClick={() => addToCart(product)} className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-3 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center gap-2">
                                    <FaShoppingCart />
                                    Add to Cart
                                </button>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                    <FaTruck /> Free Shipping
                                </span>
                                <span className="flex items-center gap-1">
                                    <FaShieldAlt /> 2-Year Warranty
                                </span>
                                <span className="flex items-center gap-1">
                                    <FaUndo /> 30-Day Returns
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};